import React from 'react';

import { Button, ButtonProps } from '@mantine/core';
import { IconBookmarks, IconPlaylistAdd } from '@tabler/icons-react';
import { useAuth } from 'context/auth-context';
import dayjs from 'dayjs';
import useCreateAthleteClass from 'hooks/athleteClass/useCreateAthleteClass';
import { useAthleteTracking } from 'hooks/mixpanel/useAthleteTracking';
import { AthleteKiloClassWrite, KiloClassAthleteAgenda, KiloClassAthleteRetrieve } from 'types';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

type AthleteClassReserveButtonProps = {
  action: 'reserve' | 'joinWaitlist';
  killoClass: KiloClassAthleteAgenda | KiloClassAthleteRetrieve;
} & ButtonProps;

const AthleteClassReserveButton = ({
  action,
  killoClass,
  ...props
}: AthleteClassReserveButtonProps): JSX.Element => {
  const { session, isLoading } = useAuth();
  const { successNotification, errorNotification } = notificationHandler();
  const athleteTracking = useAthleteTracking();

  const createAthleteClassMutation = useCreateAthleteClass();

  let label = '';
  let color = 'gray';
  let Icon = IconBookmarks;

  if (action === 'reserve') {
    label = 'Reserve';
    color = 'green.7';
    Icon = IconBookmarks;
  } else if (action === 'joinWaitlist') {
    label = 'Join Waitlist';
    color = 'kiloBlue.7';
    Icon = IconPlaylistAdd;
  }

  const handleCreateAthleteClass = (e: React.MouseEvent<HTMLElement>): void => {
    e.stopPropagation();
    if (session?.athlete && !isLoading) {
      const payload: AthleteKiloClassWrite = {
        athleteKiloClassId: uuidv4(),
        isReserved: action === 'reserve',
        isWaitlisted: action === 'joinWaitlist',
        isCheckedIn: false,
        kiloClass: killoClass.kiloClassId,
        athlete: session.athlete.athleteId,
        registrationDatetime: dayjs(new Date()).format('YYYY-MM-DDTHH:mm:ss'),
        isKioskSelfService: false,
      };
      createAthleteClassMutation.mutate(payload, {
        onSuccess: () => {
          if (action === 'reserve') {
            successNotification(
              'Reservation success',
              'You have successfully reserved a spot in the class.',
            );
            athleteTracking.trackClassReserved(
              killoClass.kiloClassId,
              killoClass.kiloClassType.name,
              killoClass.duration,
            );
          } else if (action === 'joinWaitlist') {
            successNotification(
              'Join waitlist success',
              'You have successfully joined the waitlist for the class.',
            );
          }
        },
        onError: error => {
          const errorData = error.response?.data;
          const isDailyLimit =
            errorData?.errorCode === 'CLASS_RESERVATION_LIMIT_ONE_PER_DAY' ||
            errorData?.errorCode === 'CLASS_WAITLIST_LIMIT_ONE_PER_DAY';
          if (isDailyLimit) {
            athleteTracking.trackReservationBlockedByDailyLimit(
              'classes',
              errorData?.details?.heldBy ?? 'reservation',
            );
          }
          errorNotification(
            action === 'reserve' ? 'Reservation failed' : 'Join waitlist failed',
            errorData?.message,
          );
        },
      });
    }
  };

  return (
    <Button
      sx={{
        boxShadow: `3px 6px 4px rgba(0, 0, 0, 0.25)`,
      }}
      leftIcon={<Icon />}
      onClick={e => handleCreateAthleteClass(e)}
      loading={createAthleteClassMutation.isLoading}
      color={color}
      mb={4}
      {...props}
    >
      {label}
    </Button>
  );
};

export default AthleteClassReserveButton;
